import React, { type FC, type ReactElement } from 'react';
import { NewsPageId } from '../../../../../app/pages/domain/constants/PageId';
import type { ILocale } from '../../../../i18n/locale/models/ILocale';
import { useLocale } from '../../../../i18n/locale/useLocale';
import { useTranslation } from '../../../../i18n/translate/useTranslation';
import { getLocation } from '../../../../router/hash/hashHistory';
import { useNavigationContext } from '../../context/NavigationContext';
import { StyledNavLinkItem } from '../../main/StyledNavigationItems';
import { useAlternateLocalizedLink } from './utils/useAlternateLocalizedLink';

interface ILanguageItemProps {
	locale: ILocale;
	children?: ReactElement | ReactElement[];
}
export const LanguageSwitchItem: FC<ILanguageItemProps> = ({
	locale,
	children,
}) => {
	const { t } = useTranslation();
	const { isLangSubMenuOpen, currentPageConfig } = useNavigationContext();
	const currentLocale = useLocale();
	const { hash: currentHash } = getLocation();
	const href = `${useAlternateLocalizedLink(
		currentPageConfig,
		currentLocale,
		locale,
	)}${currentHash}`;

	const newsStoriesPath = 'stories';
	const newsPaths = [
		NewsPageId.Blogs,
		newsStoriesPath,
		NewsPageId.PressReleases,
	];

	const isNewsPage = (paths: string[], pathname: string, language?: string) =>
		paths.some(
			(path) =>
				pathname.startsWith(`/news/${path}`) ||
				pathname.startsWith(`/${language}/news/${path}`),
		);

	return (
		<StyledNavLinkItem
			href={href}
			role="menuitem"
			aria-hidden={!isLangSubMenuOpen}
			forceRefresh={isNewsPage(newsPaths, href, locale.prefix)} // news pages have static content and therefore we need to perform a re-render when switching languages
		>
			{children && children}
			{locale.displayValue === 'EN'
				? t('main_nav_language_en_label')
				: t('main_nav_language_es_label')}
		</StyledNavLinkItem>
	);
};
